// redux/slices/columnPreferencesSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * Interface pour une préférence de colonne
 */
interface ColumnPreference {
  field: string
  visible: boolean
  width?: number
  order?: number  // Pour l'ordre des colonnes si besoin plus tard
}

/**
 * State : un objet avec une clé par page
 * Exemple: { 'vendor/items': [...columns], 'vendor/items/unsold': [...columns] }
 */
interface ColumnPreferencesState {
  [pageKey: string]: ColumnPreference[]
}

/**
 * État initial : vide ou chargé depuis localStorage
 */
const loadInitialState = (): ColumnPreferencesState => {
  if (typeof window === 'undefined') {
    return {} // SSR : pas de localStorage
  }

  try {
    const saved = localStorage.getItem('dodecade_column_preferences')
    if (saved) {
      const parsed = JSON.parse(saved)
      return parsed
    }
  } catch (error) {
    console.error('❌ Error loading column preferences from localStorage:', error)
  }

  return {}
}

const initialState: ColumnPreferencesState = loadInitialState()

/**
 * Helper pour sauvegarder dans localStorage
 */
const saveToLocalStorage = (state: ColumnPreferencesState) => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem('dodecade_column_preferences', JSON.stringify(state))
  } catch (error) {
    console.error('❌ Error saving column preferences to localStorage:', error)
  }
}

/**
 * Slice Redux pour gérer les préférences de colonnes
 */
export const columnPreferencesSlice = createSlice({
  name: 'columnPreferences',
  initialState,
  reducers: {
    /**
     * Définir les préférences de colonnes pour une page
     */
    setColumnPreferences: (
      state,
      action: PayloadAction<{ pageKey: string; columns: ColumnPreference[] }>
    ) => {
      const { pageKey, columns } = action.payload
      state[pageKey] = columns
      saveToLocalStorage(state)
    },

    /**
     * Charger les préférences depuis localStorage (appelé au démarrage de l'app)
     */
    loadColumnPreferences: (state) => {
      const loaded = loadInitialState()
      return loaded
    },

    /**
     * Réinitialiser les préférences d'une page spécifique
     */
    resetColumnPreferences: (state, action: PayloadAction<string>) => {
      const pageKey = action.payload
      delete state[pageKey]
      saveToLocalStorage(state)
    },

    /**
     * Tout effacer (toutes les pages)
     */
    clearAllColumnPreferences: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('dodecade_column_preferences')
      }
      return {}
    },

    /**
     * Mettre à jour la largeur d'une colonne spécifique
     */
    updateColumnWidth: (
      state,
      action: PayloadAction<{ pageKey: string; field: string; width: number }>
    ) => {
      const { pageKey, field, width } = action.payload
      const preferences = state[pageKey]

      if (preferences) {
        const column = preferences.find((col) => col.field === field)
        if (column) {
          column.width = width
          saveToLocalStorage(state)
        }
      }
    },
  },
})

// Export des actions
export const {
  setColumnPreferences,
  loadColumnPreferences,
  resetColumnPreferences,
  clearAllColumnPreferences,
  updateColumnWidth,
} = columnPreferencesSlice.actions

// Export du reducer
export default columnPreferencesSlice.reducer